<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useIntervalFn } from '@vueuse/core'
import { Loader2, RefreshCw } from 'lucide-vue-next'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import SkeletonRows from '@lib/components/shared/SkeletonRows.vue'
import { useDashboardData } from '@/composables/useDashboardData'
import DashboardKpiCards from '@/components/domain/dashboard/DashboardKpiCards.vue'
import DashboardActiveEvents from '@/components/domain/dashboard/DashboardActiveEvents.vue'
import DashboardScanFeed from '@/components/domain/dashboard/DashboardScanFeed.vue'
import DashboardAlerts from '@/components/domain/dashboard/DashboardAlerts.vue'
import DashboardQuickActions from '@/components/domain/dashboard/DashboardQuickActions.vue'
import DashboardInstancesTable from '@/components/domain/dashboard/DashboardInstancesTable.vue'

const { t } = useI18n()
const ui = useUiStore()
const auth = useAuthStore()
const { data, loading, refreshing, error, lastFetchedAt, refresh } = useDashboardData()

onMounted(() => {
    ui.setBreadcrumbs([{ label: t('nav.dashboard') }])
})

const now = ref(Date.now())
useIntervalFn(() => { now.value = Date.now() }, 1000)

const updatedSeconds = computed(() => {
    if (!lastFetchedAt.value) return 0
    return Math.max(0, Math.floor((now.value - lastFetchedAt.value.getTime()) / 1000))
})

const noPermissions = computed(() => {
    if (!data.value) return false
    const d = data.value
    return d.validationsLast24h === null
        && d.ticketsTotal === null
        && d.activeDevicesCount === null
        && d.upcomingEventsCount === null
        && d.recentScans === null
        && d.alerts === null
})
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('dashboard.title')">
            <template #actions>
                <span v-if="lastFetchedAt" class="hidden sm:inline text-xs text-gray-500">
                    {{ t('dashboard.updatedAgo', { seconds: updatedSeconds }) }}
                </span>
                <button
                    type="button"
                    :disabled="refreshing || loading"
                    :title="t('dashboard.refresh')"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 transition"
                    @click="refresh"
                >
                    <RefreshCw class="w-3.5 h-3.5" :class="{ 'animate-spin': refreshing }" />
                    <span class="hidden sm:inline">{{ t('dashboard.refresh') }}</span>
                </button>
            </template>
        </PageHeader>

        <div v-if="loading">
            <SkeletonRows :rows="6" :columns="4" />
        </div>

        <div
            v-else-if="error && !data"
            class="bg-red-50 border border-red-200 text-red-900 rounded-12 p-4"
        >
            {{ t('dashboard.error', { message: error }) }}
        </div>

        <div
            v-else-if="noPermissions"
            class="bg-white border border-gray-200 rounded-12 p-8 text-center text-gray-500"
        >
            {{ t('dashboard.noPermissions') }}
        </div>

        <div v-else-if="data" class="relative">
            <Transition name="fade">
                <div
                    v-if="refreshing"
                    class="absolute top-0 right-0 z-10 inline-flex items-center gap-2 bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-1.5 text-xs text-gray-600"
                >
                    <Loader2 class="w-3.5 h-3.5 text-teal animate-spin" />
                    {{ t('dashboard.loading') }}
                </div>
            </Transition>

            <div :class="{ 'opacity-60 transition-opacity duration-200': refreshing }">
            <DashboardKpiCards :data="data" />

            <div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div class="lg:col-span-3">
                    <DashboardActiveEvents v-if="data.activeEvents" :events="data.activeEvents" />
                    <DashboardScanFeed
                        v-if="data.recentScans"
                        :scans="data.recentScans"
                        :refreshing="refreshing"
                        :last-fetched-at="lastFetchedAt"
                        @refresh="refresh"
                    />
                </div>
                <div class="lg:col-span-2">
                    <DashboardAlerts v-if="data.alerts" :alerts="data.alerts" />
                    <DashboardQuickActions />
                </div>
            </div>

            <DashboardInstancesTable
                v-if="auth.user?.isSuperuser && data.instances"
                :instances="data.instances"
            />
            </div>
        </div>
    </DefaultLayout>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 150ms ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
